import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Activity, PhoneCall } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar({ onOpenConsultation }) {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const location = useLocation();

  // Monitor scroll level to toggle navbar design state
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => {
    setIsOpen(false);
    setShowDropdown(false);
  }, [location]);

  const navItems = [
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Products', path: '/products' },
    { name: 'Projects', path: '/projects' },
    { name: 'Blogs', path: '/blogs' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <nav className="fixed top-0 left-0 w-full z-[999] bg-white/95 backdrop-blur-md shadow-lg border-b border-brand-silver/50 py-1">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          
          {/* Logo Image */}
          <Link to="/" className="flex items-center group">
            <img 
              src="/assets/logo1.png" 
              alt="Kiran Distributors" 
              className="h-11 sm:h-13 md:h-14 lg:h-15 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation Links & CTA Grouped on the Right */}
          <div className="hidden lg:flex items-center gap-8 ml-auto">
            <div className="flex items-center gap-8">
              {navItems.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) => 
                    `nav-link text-sm font-semibold tracking-wide ${
                      isActive ? 'text-brand-blue active' : 'text-brand-navy hover:text-brand-blue'
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              ))}
            </div>

            <Link
              to="/contact"
              className="btn-primary py-2.5 px-5 text-sm flex items-center gap-2 group font-poppins text-white"
            >
              <PhoneCall size={15} className="group-hover:rotate-12 transition-transform duration-300" />
              Get Consultation
            </Link>
          </div>

          {/* Mobile Menu Toggle Button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg transition-colors text-brand-navy hover:bg-gray-100"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-b border-brand-silver/50 w-full overflow-hidden absolute top-full left-0 shadow-xl"
          >
            <div className="px-6 py-6 space-y-4 flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) => 
                    `text-base font-semibold font-poppins py-2 border-b border-gray-50 tracking-wide transition-colors ${
                      isActive 
                        ? 'text-brand-blue font-bold border-brand-blue/30' 
                        : 'text-brand-navy hover:text-brand-blue'
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              ))}

              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="w-full btn-primary py-3 mt-4 text-center justify-center flex items-center gap-2 text-white"
              >
                <PhoneCall size={16} />
                Get Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
